import { useState, type FormEvent } from "react";
import { TicketCheck } from "lucide-react";
import type { PublicPermitProduct } from "@natural/public-contract";
import { accountApi, type ConsumerMe } from "../../account";

function PermitClaim({
  products,
  onSaved,
}: {
  products: PublicPermitProduct[];
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false),
    [message, setMessage] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");
    const data = new FormData(event.currentTarget);
    try {
      await accountApi("/api/account/permit-claims", {
        method: "POST",
        body: JSON.stringify({
          permitProductId: data.get("permitProductId"),
          externalReference: data.get("externalReference"),
        }),
      });
      setMessage(
        "Lupa tallennettiin varmennettavaksi. Se ei vaikuta Core-päätökseen ennen hyväksyntää.",
      );
      onSaved();
    } catch (cause) {
      setMessage(
        cause instanceof Error ? cause.message : "Lupaa ei voitu tallentaa.",
      );
    }
  };

  if (!open) {
    return (
      <button className="secondary-action" onClick={() => setOpen(true)}>
        <TicketCheck size={17} /> Lisää olemassa oleva lupa
      </button>
    );
  }

  return (
    <form className="inline-account-form" onSubmit={submit}>
      <label>
        Lupatuote
        <select name="permitProductId" required defaultValue="">
          <option value="" disabled>Valitse julkaistu lupatuote</option>
          {products.map((product) => (
            <option value={product.id} key={product.id}>
              {product.name}{product.provider ? ` · ${product.provider}` : ""}
            </option>
          ))}
        </select>
      </label>
      <label>
        Luvan tunniste tai tilausnumero
        <input name="externalReference" minLength={2} maxLength={200} required />
      </label>
      {message && <p className="form-message">{message}</p>}
      <div>
        <button type="button" className="text-action" onClick={() => setOpen(false)}>
          Sulje
        </button>
        <button disabled={!products.length}>Lähetä varmennettavaksi</button>
      </div>
    </form>
  );
}

export function PermitsPage({
  me,
  claimProducts,
  catalogProducts,
  onSaved,
}: {
  me: ConsumerMe;
  claimProducts: PublicPermitProduct[];
  catalogProducts: PublicPermitProduct[];
  onSaved: () => void;
}) {
  const productNames = new Map(
    [...catalogProducts, ...claimProducts].map((product) => [product.id, product.name]),
  );
  return (
    <div className="account-columns consumer-tab-columns">
      <section className="account-panel" id="omat-luvat">
        <div className="account-panel-heading">
          <div>
            <span className="eyebrow">Omat luvat</span>
            <h2>Voimassaolo ja kattavuus</h2>
          </div>
          <PermitClaim products={claimProducts} onSaved={onSaved} />
        </div>
        {me.permits.length ? (
          <div className="account-item-list">
            {me.permits.map((permit) => (
              <article key={permit.id}>
                <TicketCheck />
                <div>
                  <b>{productNames.get(permit.permit_product_id) ?? permit.permit_product_id}</b>
                  <span>
                    {permit.status === "active"
                      ? "Varmennettu ja huomioidaan lupaohjeissa"
                      : permit.status === "pending"
                        ? "Odottaa varmennusta – ei vielä lupaohjeissa"
                        : "Ei voimassa"}
                  </span>
                  <small>
                    {new Date(permit.valid_from).toLocaleDateString("fi-FI")}–
                    {new Date(permit.valid_to).toLocaleDateString("fi-FI")}
                  </small>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="account-empty compact">
            <TicketCheck />
            <p>
              Ilmoita olemassa oleva lupa. Se huomioidaan henkilökohtaisessa
              lupaohjeessa varmennuksen jälkeen.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
